import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ChatService } from './chat.service';
import { ChatUiService } from './chat-ui.service';
import { ChatSheetService } from './chat-sheet.service';
import { ConversationSummary, NotifyLevel } from './core/hub-types';
import { patientFileRef } from './ui/patient-file-ref';
import { MAX_PINNED } from './ui/conversation-sections';
import { PHONE_PX } from './ui/chat-window-size';
import { ConversationInfoDialogComponent } from './components/conversation-info-dialog/conversation-info-dialog.component';

const TRY_AGAIN = 'Could not change that just now. Try again.';

/**
 * What a conversation's menu does (patient file, details, pin, notifications, mute), shared by the chat window's
 * menu and the conversation list's right-click / long-press menu.
 */
@Injectable({ providedIn: 'root' })
export class ConversationActionsService {
  constructor(
    private readonly chat: ChatService,
    private readonly ui: ChatUiService,
    private readonly sheets: ChatSheetService,
    private readonly router: Router,
    private readonly snack: MatSnackBar,
  ) {}

  /** The patient file this conversation is about, if this practice has one (patient, referral, patient-app chat). */
  fileOf(conversation: ConversationSummary | null): string | null {
    return patientFileRef(conversation);
  }

  /** Opens the patient's file behind the window; on a phone, where the window is the screen, it steps aside. */
  openFile(patientRef: string): void {
    void this.router.navigate(['/patients', { outlets: { patient: ['file', patientRef] } }]);
    if (window.innerWidth <= PHONE_PX) this.ui.setMinimized(true);
  }

  muted(conversation: ConversationSummary): boolean {
    return this.chat.store.isMuted(conversation);
  }

  /** Whether I am an admin of the practice the conversation belongs to. */
  isOrgAdmin(conversation: ConversationSummary): boolean {
    const me = this.chat.store.snapshot.me;
    return !!me && me.orgs.some((o) => o.orgId === conversation.orgId && o.role === 'admin');
  }

  /** The conversation's details and members, as a panel in the chat window. */
  info(conversation: ConversationSummary): void {
    this.sheets.open(ConversationInfoDialogComponent, { data: { conversationId: conversation.id, isOrgAdmin: this.isOrgAdmin(conversation) } });
  }

  async setNotify(conversation: ConversationSummary, level: NotifyLevel): Promise<void> {
    await this.attempt(() => this.chat.store.setNotifyLevel(conversation.id, level));
  }

  /** Mutes for `hours`, until unmuted (`'always'`: a hundred years), or unmutes (`null`). */
  async mute(conversation: ConversationSummary, hours: number | 'always' | null): Promise<void> {
    const until = hours === null ? null : new Date(Date.now() + (hours === 'always' ? 100 * 365 * 24 : hours) * 3_600_000);
    await this.attempt(() => this.chat.store.mute(conversation.id, until));
  }

  /** Pins the conversation to the top of my list, or unpins it; at most five per practice. */
  async pin(conversation: ConversationSummary, pinned: boolean): Promise<void> {
    try {
      await this.chat.store.pinConversation(conversation.id, pinned);
    } catch (e) {
      const full = (e as { code?: string })?.code === 'PIN_LIMIT';
      this.snack.open(full ? `You can pin up to ${MAX_PINNED} chats. Unpin one first.` : TRY_AGAIN, 'OK', { duration: 4000 });
    }
  }

  private async attempt(change: () => Promise<unknown>): Promise<void> {
    try {
      await change();
    } catch {
      this.snack.open(TRY_AGAIN, 'OK', { duration: 4000 });
    }
  }
}
